import { useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../lib/auth';

export type ActivityType = 
  | 'like' 
  | 'comment' 
  | 'share' 
  | 'bookmark' 
  | 'follow' 
  | 'unfollow'
  | 'post' 
  | 'view'
  | 'profile_visit'
  | 'search'
  | 'login'
  | 'logout';

export type InteractionType = 
  | 'like'
  | 'comment' 
  | 'share'
  | 'bookmark'
  | 'view';

export interface ActivityTarget {
  id: string;
  type: 'post' | 'comment' | 'user' | 'page';
  title?: string;
  username?: string;
  thumbnail?: string;
}

export interface ActivityMetadata {
  device?: string;
  location?: string;
  ip_address?: string;
  user_agent?: string;
  referrer?: string;
  [key: string]: any;
}

export const useActivityTracking = () => {
  const { user } = useAuth();

  // Log general user activity
  const logActivity = useCallback(async (
    activityType: ActivityType,
    target?: ActivityTarget,
    content?: string,
    metadata?: ActivityMetadata
  ) => {
    if (!user?.id) return;

    try {
      const { error } = await supabase.rpc('log_user_activity', {
        p_user_id: user.id,
        p_activity_type: activityType,
        p_target_type: target?.type || null,
        p_target_id: target?.id || null,
        p_target_title: target?.title || null,
        p_target_username: target?.username || null,
        p_target_thumbnail: target?.thumbnail || null,
        p_content: content || null,
        p_metadata: metadata || null
      });

      if (error) {
        // Silently handle errors for activity tracking (non-critical)
        console.warn('Activity tracking error:', error);
        return;
      }
    } catch (error) {
      console.warn('Activity tracking failed:', error);
    }
  }, [user?.id]);

  // Track post interactions (like, share, bookmark, etc.)
  const trackPostInteraction = useCallback(async (
    postId: string,
    interactionType: InteractionType,
    content?: string,
    postTitle?: string,
    postThumbnail?: string
  ) => {
    if (!user?.id) return false;

    try {
      const { data, error } = await supabase.rpc('toggle_post_interaction', {
        p_user_id: user.id,
        p_post_id: postId,
        p_interaction_type: interactionType,
        p_content: content || null
      });

      if (error) {
        console.warn('Post interaction tracking error:', error);
        return false;
      }

      // Also log in general activity
      await logActivity(interactionType as ActivityType, {
        id: postId,
        type: 'post',
        title: postTitle,
        thumbnail: postThumbnail
      }, content);

      return data; // Returns true if added, false if removed
    } catch (error) {
      console.warn('Post interaction tracking failed:', error);
      return false;
    }
  }, [user?.id, logActivity]);

  // Track user follows
  const trackFollow = useCallback(async (
    targetUserId: string,
    targetUsername?: string,
    targetThumbnail?: string
  ) => {
    if (!user?.id) return false;

    try {
      // Check if already following
      const { data: existingFollow } = await supabase
        .from('user_follows')
        .select('id')
        .eq('follower_id', user.id)
        .eq('following_id', targetUserId)
        .single();

      if (existingFollow) {
        // Unfollow
        const { error } = await supabase
          .from('user_follows')
          .delete()
          .eq('follower_id', user.id)
          .eq('following_id', targetUserId);

        if (error) throw error;

        // Log unfollow activity
        await logActivity('unfollow', {
          id: targetUserId,
          type: 'user',
          username: targetUsername,
          thumbnail: targetThumbnail
        });

        return false; // Unfollowed
      } else {
        // Follow
        const { error } = await supabase
          .from('user_follows')
          .insert({
            follower_id: user.id,
            following_id: targetUserId
          });

        if (error) throw error;

        // Log follow activity
        await logActivity('follow', {
          id: targetUserId,
          type: 'user',
          username: targetUsername,
          thumbnail: targetThumbnail
        });

        return true; // Followed
      }
    } catch (error) {
      console.warn('Follow tracking failed:', error);
      return false;
    }
  }, [user?.id, logActivity]);

  // Track page/profile views
  const trackView = useCallback(async (
    target: ActivityTarget,
    duration?: number
  ) => {
    if (!user?.id) return;

    await logActivity('view', target, undefined, {
      duration,
      timestamp: new Date().toISOString()
    });

    // Also track in post interactions if it's a post
    if (target.type === 'post') {
      await trackPostInteraction(target.id, 'view', undefined, target.title, target.thumbnail);
    }
  }, [user?.id, logActivity, trackPostInteraction]);

  // Track search queries
  const trackSearch = useCallback(async (
    query: string,
    resultsCount?: number,
    filterType?: string
  ) => {
    if (!user?.id) return;

    await logActivity('search', undefined, query, {
      results_count: resultsCount,
      filter_type: filterType,
      timestamp: new Date().toISOString()
    });
  }, [user?.id, logActivity]);

  // Get user's interaction status for a post
  const getPostInteractionStatus = useCallback(async (postId: string) => {
    if (!user?.id) return {};

    try {
      const { data, error } = await supabase
        .from('posts_interactions')
        .select('interaction_type')
        .eq('user_id', user.id)
        .eq('post_id', postId);

      if (error) throw error;

      const interactions: Record<string, boolean> = {};
      data?.forEach(interaction => {
        interactions[interaction.interaction_type] = true;
      });

      return interactions;
    } catch (error) {
      console.warn('Failed to get post interaction status:', error);
      return {};
    }
  }, [user?.id]);

  // Check if user is following another user
  const isFollowing = useCallback(async (targetUserId: string) => {
    if (!user?.id) return false;

    try {
      const { data, error } = await supabase
        .from('user_follows')
        .select('id')
        .eq('follower_id', user.id)
        .eq('following_id', targetUserId)
        .single();

      if (error && error.code !== 'PGRST116') throw error;

      return !!data;
    } catch (error) {
      console.warn('Failed to check follow status:', error);
      return false;
    }
  }, [user?.id]);

  return {
    logActivity,
    trackPostInteraction,
    trackFollow,
    trackView,
    trackSearch,
    getPostInteractionStatus,
    isFollowing
  };
};
